import { ref, computed, onUnmounted, type Ref } from 'vue';
import { aiService } from '@/services/AIService';
import type { ModelInvocation, ModelInvocationStatus, ModelInvocationStatusResponse } from '@/types/ai';

// Exponential backoff intervals: 1s, 2s, 4s, 8s, 16s, 30s, 60s, 2min (max)
const BACKOFF_INTERVALS = [1000, 2000, 4000, 8000, 16000, 30000, 60000, 120000];

// PENDING renders as QUEUED: invocations wait in the SQS queue by design (CAC-5607)
// and "pending" reads as stuck, while "queued" reads as work continuing in background.
export const getInvocationStatusLabel = (status: ModelInvocationStatus): string => {
  return status === 'PENDING' ? 'QUEUED' : status;
};

export function useModelInvocation() {
  // Per-instance registries. These used to be module-level singletons, so one
  // component's stopAllPolling() silently killed every other component's polls.
  const pollingIntervals = new Map<string, ReturnType<typeof setTimeout>>();
  const activePollIds = new Set<string>();
  // Bumped whenever a poll is cancelled. clearTimeout cannot reach a poll that is
  // sitting in its await, and on resume that poll would re-register itself; comparing
  // generations lets it notice it was stopped and decline to re-arm.
  const pollGenerations = new Map<string, number>();

  const isPolling = ref(false);
  let isUnmounted = false;

  const pollInvocationStatus = (
    invocationId: string,
    onComplete: (result: ModelInvocationStatusResponse) => void,
    onError: (error: Error) => void,
    onStatusUpdate?: (status: ModelInvocationStatusResponse) => void
  ) => {
    if (isUnmounted) return;
    if (activePollIds.has(invocationId) || pollingIntervals.has(invocationId)) {
      // Already polling this invocation
      return;
    }
    activePollIds.add(invocationId);

    const generation = (pollGenerations.get(invocationId) ?? 0) + 1;
    pollGenerations.set(invocationId, generation);

    isPolling.value = true;
    let attempt = 0;

    const isCancelled = () => isUnmounted || pollGenerations.get(invocationId) !== generation;

    const armNext = () => {
      attempt++;
      const intervalIndex = Math.min(attempt, BACKOFF_INTERVALS.length - 1);
      activePollIds.delete(invocationId);
      const timeoutId = setTimeout(poll, BACKOFF_INTERVALS[intervalIndex]);
      pollingIntervals.set(invocationId, timeoutId);
    };

    const poll = async () => {
      try {
        const status = await aiService.getInvocationStatus(invocationId);
        if (isCancelled()) return;

        // Call status update callback if provided
        if (onStatusUpdate) {
          onStatusUpdate(status);
        }

        // Check if completed or failed
        if (status.status === 'COMPLETED' || status.status === 'FAILED') {
          stopPollingInvocation(invocationId);
          onComplete(status);
          return;
        }

        // Continue polling with exponential backoff
        armNext();
      } catch (error) {
        if (isCancelled()) return;

        // If we've tried too many times, stop and call error handler
        if (attempt + 1 >= BACKOFF_INTERVALS.length * 2) {
          stopPollingInvocation(invocationId);
          onError(error instanceof Error ? error : new Error('Polling failed'));
          return;
        }

        // On error, retry with backoff
        armNext();
      }
    };

    // Start polling immediately
    poll();
  };

  const stopPollingInvocation = (invocationId: string) => {
    // Bumping the generation cancels a poll that is currently mid-await, which
    // clearTimeout alone cannot do.
    pollGenerations.set(invocationId, (pollGenerations.get(invocationId) ?? 0) + 1);

    activePollIds.delete(invocationId);
    const intervalId = pollingIntervals.get(invocationId);
    if (intervalId) {
      clearTimeout(intervalId);
      pollingIntervals.delete(invocationId);
    }

    // Update isPolling if no more active polls
    if (activePollIds.size === 0 && pollingIntervals.size === 0) {
      isPolling.value = false;
    }
  };

  const stopAllPolling = () => {
    pollGenerations.forEach((generation, invocationId) => {
      pollGenerations.set(invocationId, generation + 1);
    });
    activePollIds.clear();
    pollingIntervals.forEach((intervalId) => {
      clearTimeout(intervalId);
    });
    pollingIntervals.clear();
    isPolling.value = false;
  };

  // Cleanup on unmount
  onUnmounted(() => {
    isUnmounted = true;
    stopAllPolling();
  });

  return {
    isPolling,
    pollInvocationStatus,
    stopPollingInvocation,
    stopAllPolling,
  };
}

// Above the enter threshold, per-invocation status polling becomes a request storm
// (N parallel poll loops). Large batches instead refresh the active-invocations list
// on one fixed interval and reload results as items finish (CAC-5607). Below the exit
// threshold the per-invocation polling gives snappier updates. The gap between the two
// keeps a batch hovering at the boundary from tearing the strategy down and rebuilding
// it on every tick.
const AGGREGATE_ENTER_THRESHOLD = 10;
const AGGREGATE_EXIT_THRESHOLD = 6;
const AGGREGATE_POLL_INTERVAL_MS = 10000;
export const ACTIVE_INVOCATIONS_LIMIT = 100; // the endpoint caps the limit param at 100

interface AggregatePollingOptions {
  /** The page's active-invocation list; replaced wholesale on each aggregate tick. */
  activeInvocations: Ref<ModelInvocation[]>;
  /** Reload the page's result lists once per tick in which anything finished. */
  onFinished: () => Promise<void> | void;
  /** Start a per-invocation poll (individual mode only). */
  startIndividualPoll: (invocationId: string) => void;
  /** Tear down every per-invocation poll when switching into aggregate mode. */
  stopAllIndividualPolls: () => void;
  /**
   * Keep FAILED invocations in the list after they leave the active endpoint (which
   * returns only PENDING/RUNNING). Only for pages with no other way to surface a
   * failure — a page that also renders /extraction-failures already gets those rows
   * from there and would show each failure twice.
   */
  retainFailures?: boolean;
}

/**
 * Owns the choice between per-invocation polling and one aggregate refresh loop, so
 * both results pages share a single implementation of the CAC-5607 behaviour.
 */
export function useAggregateInvocationPolling(options: AggregatePollingOptions) {
  const {
    activeInvocations,
    onFinished,
    startIndividualPoll,
    stopAllIndividualPolls,
    retainFailures = false,
  } = options;

  const inProgressInvocations = computed(() =>
    activeInvocations.value.filter((inv) => inv.status === 'PENDING' || inv.status === 'RUNNING'),
  );
  const queuedCount = computed(() => inProgressInvocations.value.filter((inv) => inv.status === 'PENDING').length);
  const runningCount = computed(() => inProgressInvocations.value.filter((inv) => inv.status === 'RUNNING').length);

  let aggregatePollTimer: ReturnType<typeof setInterval> | null = null;
  let isRefreshing = false;
  let isUnmounted = false;

  const isAggregatePolling = () => aggregatePollTimer !== null;

  const stopAggregatePolling = () => {
    if (aggregatePollTimer) {
      clearInterval(aggregatePollTimer);
      aggregatePollTimer = null;
    }
  };

  // Pick the polling strategy for the current in-flight count, tearing down the other.
  // Sole owner of starting per-invocation polls, so callers only have to call this.
  const syncPollingMode = () => {
    if (isUnmounted) return;

    const activeCount = inProgressInvocations.value.length;
    if (!aggregatePollTimer && activeCount > AGGREGATE_ENTER_THRESHOLD) {
      stopAllIndividualPolls();
      aggregatePollTimer = setInterval(aggregateRefresh, AGGREGATE_POLL_INTERVAL_MS);
      return;
    }
    if (aggregatePollTimer && activeCount <= AGGREGATE_EXIT_THRESHOLD) {
      stopAggregatePolling();
    }
    if (!aggregatePollTimer) {
      // pollInvocationStatus ignores ids it is already polling, so this is idempotent.
      inProgressInvocations.value.forEach((inv) => startIndividualPoll(inv.invocationId));
    }
  };

  const aggregateRefresh = async () => {
    // setInterval does not await, so without this a slow tick overlaps the next one and
    // whichever response lands last wins — including an older snapshot.
    if (isRefreshing || isUnmounted) return;
    isRefreshing = true;

    const before = new Map(inProgressInvocations.value.map((inv) => [inv.invocationId, inv]));
    let anyFinished = false;

    try {
      const fetched = (await aiService.getActiveInvocations(ACTIVE_INVOCATIONS_LIMIT)) || [];
      if (isUnmounted) return;

      const fetchedIds = new Set(fetched.map((inv) => inv.invocationId));

      // Anything that left the in-flight set finished. COMPLETED ones come back through
      // the results reload; FAILED ones are filtered out of the active endpoint.
      const finishedIds = [...before.keys()].filter((id) => !fetchedIds.has(id));
      anyFinished = finishedIds.length > 0;

      // Without retention a failure would just vanish from the list. Ask each finished
      // invocation for its final status and keep the failures on screen with their error
      // message, alongside any already showing.
      const retained: ModelInvocation[] = [];
      if (retainFailures) {
        retained.push(
          ...activeInvocations.value.filter(
            (inv) => inv.status === 'FAILED' && !fetchedIds.has(inv.invocationId),
          ),
        );

        if (anyFinished) {
          const settled = await Promise.all(
            finishedIds.map((id) => aiService.getInvocationStatus(id).then((status) => ({ id, status }), () => null)),
          );
          if (isUnmounted) return;

          settled.forEach((entry) => {
            if (!entry || entry.status.status !== 'FAILED') return;
            const original = before.get(entry.id);
            if (!original) return;
            retained.push({
              ...original,
              status: 'FAILED',
              errorMessage: entry.status.errorMessage ?? original.errorMessage,
            });
          });
        }
      }

      activeInvocations.value = [...fetched, ...retained];
    } catch (err: any) {
      console.error('Aggregate invocation refresh failed:', err);
      return; // keep polling; transient errors resolve on the next tick
    } finally {
      isRefreshing = false;
    }

    if (anyFinished) {
      // Kept out of the block above so a reload failure can't abort the mode sync, and
      // caught so it can't surface as an unhandled rejection from the interval callback.
      try {
        await onFinished();
      } catch (err: any) {
        console.error('Reload after finished invocations failed:', err);
      }
      if (isUnmounted) return;
    }

    syncPollingMode();
  };

  onUnmounted(() => {
    // Without this an in-flight refresh would resolve after unmount and hand
    // syncPollingMode a null timer, re-creating the interval on a dead component.
    isUnmounted = true;
    stopAggregatePolling();
  });

  return {
    inProgressInvocations,
    queuedCount,
    runningCount,
    isAggregatePolling,
    syncPollingMode,
  };
}
